import {
  ChangeDetectionStrategy,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  QueryList,
  SimpleChanges,
  ViewChild,
  ViewChildren,
} from '@angular/core';
import { getFullShipToAddress } from '@epicuro-next/utilities';
import { Account, Address } from 'graphql-api/gql-api-interfaces';

import { getAddressUniqueId } from '../../shared/get-address-unique-id/get-address-unique-id';
import { SelectionAccountPayload, SelectionAddressPayload } from '../../types';

@Component({
  changeDetection: ChangeDetectionStrategy.OnPush,
  selector: 'epc-accounts-form',
  templateUrl: './accounts-form.component.html',
  styleUrls: ['./accounts-form.component.scss'],
  standalone: false,
})
export class AccountsFormComponent implements OnChanges {
  /** Form element list */
  @ViewChild('accountList') public listElement: ElementRef;

  @Input() public selectedAddressIds: string[] = [];
  @Input() public selectedAccountIds: string[] = [];

  @Input() public currentBasketAccountIds: string[];

  @Input() public loading: boolean;

  @Input() public searchValue: string;

  @Input() public shouldHighlightAddress: boolean;

  /** Accounts list */
  @Input() public accounts: Account[] = [];

  @Input() public isAccountAddressReverted = true;

  /** Flag shows selected accounts to be on top */
  @Input() public shouldScrollToTheTop: boolean;

  /** Flag shows empty accounts list for search query */
  @Input() public noSearchResult = false;

  /** Current active accountId */
  @Input() public isSingleSelection = true;

  @Input() public isMoveBasketEnabled = false;

  /** Event that emits set next pagination page to load accounts */
  @Output() public setNextPage = new EventEmitter();

  @Output()
  public toggleAddressSelection = new EventEmitter<SelectionAddressPayload>();

  @Output()
  public toggleAccountSelection = new EventEmitter<SelectionAccountPayload>();

  @ViewChildren('addressPartRef')
  public addressParts: QueryList<ElementRef>;

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.accounts?.currentValue && this.shouldScrollToTheTop) {
      this.scrollToTopOfList();
      this.shouldScrollToTheTop = false;
    }
  }

  public getFullShipToAddress(address: Address) {
    return getFullShipToAddress(address, {
      revertAddress: this.isAccountAddressReverted,
      countryCodePreferred: true,
    });
  }

  public trackByAccountId(_: number, account: Account): string {
    return account.accountId;
  }

  public handleAddressSelectionChange(
    event: Event,
    address: Address,
    account: Account,
  ) {
    this.toggleAddressSelection.emit({
      account,
      address,
      isSelected: (event.target as HTMLInputElement).checked,
      isSingleSelection: this.isSingleSelection,
    });
  }

  public handleAccountSelectionChange(event: Event, account: Account) {
    const isSelected =
      this.isSingleSelection ||
      this.isIndeterminate(account) ||
      !this.isAccountSelected(account);

    (event.target as HTMLInputElement).checked = isSelected;

    this.toggleAccountSelection.emit({
      account,
      isSelected,
      isSingleSelection: this.isSingleSelection,
    });
  }

  public isAddressSelected(address: Address) {
    return this.selectedAddressIds.includes(getAddressUniqueId(address));
  }

  public isAccountSelected(account: Account) {
    return this.selectedAccountIds.includes(account.accountId);
  }

  public isIndeterminate(account: Account) {
    const addressIds = account.shipToAddresses.map(getAddressUniqueId);
    let indexMatch: boolean | undefined;
    return addressIds.some((id) => {
      const included = this.selectedAddressIds.includes(id);
      if (indexMatch === undefined) {
        indexMatch = included;
      }
      return (included && !indexMatch) || (!included && indexMatch);
    });
  }

  /** Set next pagination page */
  public onScroll() {
    this.setNextPage.emit();
  }

  /** Scroll to the top of accounts list */
  private scrollToTopOfList() {
    this.listElement?.nativeElement.querySelector('article')?.scrollIntoView();
  }

  public isCurrentBasketAccount(account: Account) {
    return this.currentBasketAccountIds.includes(account.accountId);
  }

  public highlightCb(ref: ElementRef, isFound: boolean) {
    ref.nativeElement.classList.toggle('highlighted', isFound);
  }
}
